import React from 'react';
import { Lock, Sparkles } from 'lucide-react';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';

interface FeatureLockProps {
  feature: 'ai_assistance' | 'asset_management' | 'qr_codes' | 'api' | 'sso' | 'forms' | 'checklists' | 'messaging' | 'assets';
  children: React.ReactNode;
  showUpgradePrompt?: boolean;
  customMessage?: string;
}

/**
 * FeatureLock component that wraps features and shows a lock/upgrade prompt
 * if the organization doesn't have access to the feature
 */
export default function FeatureLock({ 
  feature, 
  children, 
  showUpgradePrompt = true,
  customMessage 
}: FeatureLockProps) {
  const { hasFeature, hasActiveSubscription } = useSubscription();
  const navigate = useNavigate();

  const canAccess = hasFeature(feature);

  // Feature names for display
  const featureNames: Record<typeof feature, string> = {
    ai_assistance: 'AI Assistance',
    asset_management: 'Asset Management',
    qr_codes: 'QR Codes',
    api: 'API Access',
    sso: 'SSO',
    forms: 'Forms',
    checklists: 'Checklists',
    messaging: 'Messaging',
    assets: 'Assets',
  };

  const requiredPlans: Record<typeof feature, string> = {
    ai_assistance: 'Standard or Pro',
    asset_management: 'Standard or Pro',
    qr_codes: 'Standard or Pro',
    api: 'Pro',
    sso: 'Pro',
    forms: 'Standard or Pro',
    checklists: 'Standard or Pro',
    messaging: 'Standard or Pro',
    assets: 'Standard or Pro',
  };

  if (canAccess) {
    return <>{children}</>;
  }

  if (!showUpgradePrompt) {
    return null;
  }

  return (
    <div className="relative">
      {/* Overlay with blur effect */}
      <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center min-h-[200px]">
        <div className="text-center p-6 max-w-md">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {featureNames[feature]} Locked
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {customMessage || `This feature requires a ${requiredPlans[feature]} plan subscription.`}
          </p>
          {!hasActiveSubscription && (
            <p className="text-xs text-gray-500 mb-4">
              Your organization needs an active subscription to access premium features.
            </p>
          )}
          <button
            onClick={() => navigate('/subscription')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium inline-flex items-center gap-2"
          >
            <Sparkles className="w-4 h-4" />
            Upgrade Plan
          </button>
        </div>
      </div>
      {/* Blurred content behind overlay */}
      <div className="filter blur-sm pointer-events-none opacity-50">
        {children}
      </div>
    </div>
  );
}

